import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import type { DataWithLastModified, FigureData, SpecificationDataService, TermData } from '../specification-data-service';
import { GetFiguresApi, type GetFiguresFigure } from '../../apis/get-figures';
import { GetTermsApi } from '../../apis/get-terms';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { generateUUID } from '@telecom/shared/utils/uuid';

@injectable()
export class SpecificationDataServiceImpl implements SpecificationDataService {
    @inject(GetFiguresApi)
    private _getFiguresApi!: GetFiguresApi;

    @inject(GetTermsApi)
    private _getTermsApi!: GetTermsApi;

    @inject(CurrentTaskService)
    private _currentTaskService!: CurrentTaskService;

    private _getScale(figure: GetFiguresFigure) {
        return figure.image.properties?.scale;
    }

    async fetchFigures(): Promise<DataWithLastModified<FigureData[]>> {
        const taskId = this._currentTaskService.taskId;
        const { figures } = await this._getFiguresApi.getFigures({ task_id: taskId });
        // 检查是否所有图片都不是 abstract 的
        const hasAbstractFigure = figures.some(fig => !!fig.abstract_figure);

        const data: FigureData[] = figures.map((fig, idx) => {
            const result: FigureData = {
                id: fig.unique_id,
                order: idx + 1,
                url: fig.image.url,
                s3Key: fig.image.s3_key,
                title: `附图${idx + 1}`,
                scale: this._getScale(fig),
                rotation: typeof fig.image.properties?.rotation === 'number' ? fig.image.properties.rotation : 0,
                x: fig.image.properties?.x,
                y: fig.image.properties?.y,
                isAbstract: !!fig.abstract_figure || (!hasAbstractFigure && idx === 0),
                annotationImageBase64: fig.annotation_image_base64,
                annotations: (fig.annotations || []).map(a => ({
                    id: generateUUID(),
                    termId: a.term_unique_id,
                    type: a.properties.type,
                    attrs: a.properties.attrs,
                })),
            };
            return result;
        });
        return {
            lastModified: Date.now(),
            data,
        };
    }

    async fetchTerms(): Promise<DataWithLastModified<TermData[]>> {
        const taskId = this._currentTaskService.taskId;
        const { terms } = await this._getTermsApi.getTerms({ task_id: taskId });
        const data: TermData[] = terms.map(term => ({
            id: term.unique_id,
            text: term.name,
            number: term.number,
            annotated: term.annotated,
        }));
        return {
            lastModified: Date.now(),
            data,
        };
    }
}
